import type { OrgMineList } from '@feral/shared/client'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api.js'
import { useSession } from '../../lib/session.js'

/** Partners (§5.18, decision 211): the query keys and the tabs of Partner tools. */
export const MY_PARTNERS_KEY = ['partners', 'mine'] as const
export const partnerKey = (id: string) => ['partner', id] as const
export const partnerPageKey = (id: string) => ['partner-page', id] as const
export const partnerIntegrationsKey = (id: string) => ['partner', id, 'integrations'] as const
export const MY_SHARES_KEY = ['shares'] as const

export const PARTNER_TABS = ['about', 'admins', 'spot', 'events', 'groups', 'integrations'] as const
export type PartnerTab = (typeof PARTNER_TABS)[number]
export type PartnerSearch = { tab?: PartnerTab }
export function partnerSearch(s: Record<string, unknown>): PartnerSearch {
  return { tab: PARTNER_TABS.includes(s.tab as PartnerTab) ? (s.tab as PartnerTab) : undefined }
}

/** The partners you run, for the You row and the event form's Host segment; empty for anyone not Trusted. */
export function useMyPartners(): OrgMineList | undefined {
  const { me } = useSession()
  const trusted = !!me && (me.state === 'trusted' || me.state === 'restricted')
  return useQuery({ queryKey: MY_PARTNERS_KEY, queryFn: api.myPartners, enabled: trusted, staleTime: 60_000 })
    .data
}

/** A spot a venue could claim (FR-PRT-02): one with a business behind it (the api's lib/orgs.ts agrees). */
export const CLAIMABLE = new Set(['sauna', 'bar', 'store', 'arcade', 'gym', 'locker_room', 'hotel', 'other'])

export const dayText = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString([], { month: 'short', day: 'numeric' })
export const closureText = (c: { from: string; to: string; note: string | null }) =>
  `Closed ${c.from === c.to ? dayText(c.from) : `${dayText(c.from)} to ${dayText(c.to)}`}${c.note ? `: ${c.note}` : ''}`
